/**
 * scaled.json schema (M3 plan R8, §2.7): the scaled-space pass's scale, depth range, sphere LOD table, star and
 * planet looks, atmosphere shells, rings and the render-target resolution per preset. Every value is a starting
 * value for playtest tuning; the pass exposes the look values on the dev panel.
 */
import { z } from 'zod'
import type { BodyKind } from '../../galaxy/types'
import type { QualityPreset } from '../contracts'
import scaledJson from './scaled.json'

const hex = z.string().regex(/^#[0-9A-Fa-f]{6}$/)

const planetLook = z.object({
  /**
   * Four colours per kind. rocky: [ocean, lowland, highland, polar ice]; icy: [shadowed ice, bright ice, cracks,
   * frost]; lava: [crust, crust highlight, crack glow, crack core]; gas: [dark belt, mid belt, light zone, accent].
   */
  palette: z.tuple([hex, hex, hex, hex]),
  noise: z.object({
    /** Base noise frequency on the unit sphere. */
    scale: z.number().positive(),
    octaves: z.number().int().min(1).max(8),
    gain: z.number().positive().max(1),
    /** rocky: sea level shift; icy/lava: crack width; gas: band warp amount. */
    threshold: z.number().nonnegative(),
    /** rocky: detail frequency; icy/lava: crack frequency; gas: band count. */
    detail: z.number().positive(),
  }),
  roughness: z.number().min(0).max(1),
  /** HDR multiplier on the crack glow (lava only; 0 elsewhere). */
  emissive: z.number().nonnegative(),
})

const atmosphereLook = z.object({
  /** Shell radius in planet radii. */
  scale: z.number().min(1).max(1.5),
  color: hex,
  /** Fresnel exponent: higher = a thinner limb. */
  power: z.number().positive(),
  intensity: z.number().nonnegative(),
})

const perKind = <T extends z.ZodType>(t: T): z.ZodObject<{ rocky: T; icy: T; lava: T; gas: T }> =>
  z.object({ rocky: t, icy: t, lava: t, gas: t })

export const scaledConfigSchema = z
  .object({
    /** Scaled units per metre (1:1000). */
    scale: z.number().positive().max(1),
    /** Scaled camera near and far planes (scaled units, reversed-Z). */
    near: z.number().positive(),
    far: z.number().positive(),
    /** Render-target size as a fraction of the drawing buffer, per preset. */
    resolution: z.object({ low: z.number().min(0.1).max(1), medium: z.number().min(0.1).max(1), high: z.number().min(0.1).max(1) }),
    lod: z
      .object({
        /** Angular-radius thresholds (radians), ascending: below thresholdsRad[i] uses segments[i]. */
        thresholdsRad: z.array(z.number().positive()).min(1),
        /** Sphere width segments (height = half), one more entry than thresholds. */
        segments: z.array(z.number().int().min(8).max(512)).min(2),
      })
      .refine((l) => l.segments.length === l.thresholdsRad.length + 1, 'lod.segments needs one entry more than lod.thresholdsRad')
      .refine((l) => l.thresholdsRad.every((t, i) => i === 0 || t > l.thresholdsRad[i - 1]), 'lod.thresholdsRad must ascend'),
    light: z.object({
      /** Point-light intensity (decay 0) at luminosity 1. */
      intensity: z.number().nonnegative(),
      /** intensity × clamp(luminosity ^ exponent, minScale, maxScale). */
      luminosityExponent: z.number().nonnegative(),
      minScale: z.number().nonnegative(),
      maxScale: z.number().positive(),
      ambient: z.number().nonnegative(),
    }),
    star: z.object({
      /** HDR multiplier on the star's colour (bloom catches it). */
      intensity: z.number().positive(),
      limbDarkening: z.number().min(0).max(1),
      granulation: z.object({ scale: z.number().positive(), amount: z.number().min(0).max(1), speed: z.number().nonnegative() }),
      /** Corona billboard: half-size in star radii, HDR intensity, radial falloff exponent. */
      corona: z.object({ scale: z.number().min(1), intensity: z.number().nonnegative(), falloff: z.number().positive() }),
      /** Glare sprite: radius as a fraction of the screen height, core width (0..1 of that radius). */
      glare: z.object({ maxRadius: z.number().positive().max(1), core: z.number().positive().max(1), intensity: z.number().nonnegative(), color: hex }),
      /** Lens-flare streak: total length as a fraction of the screen width, thickness of the screen height. */
      streak: z.object({ length: z.number().positive().max(1), thickness: z.number().positive().max(0.1), intensity: z.number().nonnegative(), color: hex }),
    }),
    planets: perKind(planetLook),
    atmosphere: perKind(atmosphereLook),
    /**
     * Minimum on-screen size (D-TBD game-scale planets): a planet never draws smaller than `radiusPx` CSS pixels of
     * radius; while it is held up, its atmosphere shell widens toward `glowScale` planet radii so it reads as a lit
     * world, not a dot.
     */
    minSize: z.object({ radiusPx: z.number().nonnegative(), glowScale: z.number().min(1).max(4) }),
    rings: z
      .object({
        /** Inner and outer radius in planet radii. */
        inner: z.number().min(1),
        outer: z.number().min(1),
        color: hex,
        opacity: z.number().min(0).max(1),
        /** Radial band frequency across the ring. */
        bands: z.number().positive(),
        /** RingGeometry theta segments. */
        segments: z.number().int().min(16).max(512),
      })
      .refine((r) => r.outer > r.inner, 'rings.outer must exceed rings.inner'),
  })
  .refine((c) => c.far > c.near, 'far must exceed near')

export type ScaledConfig = z.infer<typeof scaledConfigSchema>
export type PlanetLook = z.infer<typeof planetLook>
export type AtmosphereLook = z.infer<typeof atmosphereLook>

let cached: ScaledConfig | null = null

/** Parses scaled.json once per process (throws naming the bad path). Returns a mutable copy for the dev panel. */
export function loadScaledConfig(): ScaledConfig {
  cached ??= scaledConfigSchema.parse(scaledJson)
  return structuredClone(cached)
}

export function resolutionFor(cfg: ScaledConfig, preset: QualityPreset): number {
  return cfg.resolution[preset]
}

export function planetLookFor(cfg: ScaledConfig, kind: BodyKind): PlanetLook {
  return cfg.planets[kind]
}

export function atmosphereLookFor(cfg: ScaledConfig, kind: BodyKind): AtmosphereLook {
  return cfg.atmosphere[kind]
}
